<script setup>
import { ref } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'

import 'swiper/css'

const asset = (path) => `${import.meta.env.BASE_URL}${path}`

const swiperInstance = ref(null)
const canSlidePrev = ref(false)
const canSlideNext = ref(true)

const providerCards = [
  {
    logo: 'providers/provider-1.svg',
    name: '3 Oaks Gaming',
    games: [1, 5, 9, 12, 3, 14],
  },
  {
    logo: 'providers/provider-2.svg',
    name: 'Ainsworth',
    games: [6, 11, 4, 15, 8, 2],
  },
  {
    logo: 'providers/provider-3.svg',
    name: 'Asia Gaming',
    games: [7, 13, 10, 4, 2, 9],
  },
  {
    logo: 'providers/provider-4.svg',
    name: 'Booming Games',
    games: [1, 6, 8, 10, 12, 5],
  },
]

function setSwiper(swiper) {
  swiperInstance.value = swiper
  updateNavigation(swiper)
}

function updateNavigation(swiper = swiperInstance.value) {
  if (!swiper) return

  canSlidePrev.value = !swiper.isBeginning
  canSlideNext.value = !swiper.isEnd
}

function slidePrev() {
  swiperInstance.value?.slidePrev()
}

function slideNext() {
  swiperInstance.value?.slideNext()
}
</script>

<template>
  <section class="top-providers" aria-labelledby="top-providers-title">
    <div class="top-providers__inner">
      <div class="top-providers__header">
        <h2 id="top-providers-title" class="top-providers__title">Top Providers</h2>

        <div class="top-providers__actions">
          <button
            class="top-providers__arrow"
            type="button"
            aria-label="Previous providers"
            :disabled="!canSlidePrev"
            @click="slidePrev"
          >
            <span class="top-providers__arrow-icon top-providers__arrow-icon--prev" aria-hidden="true"></span>
          </button>

          <button
            class="top-providers__arrow"
            type="button"
            aria-label="Next providers"
            :disabled="!canSlideNext"
            @click="slideNext"
          >
            <span class="top-providers__arrow-icon top-providers__arrow-icon--next" aria-hidden="true"></span>
          </button>
        </div>
      </div>

      <div
        class="top-providers__slider-frame app-swiper-fade"
        :class="{
          'app-swiper-fade--left': canSlidePrev,
          'app-swiper-fade--right': canSlideNext,
        }"
      >
        <Swiper
          class="top-providers__slider"
          :slides-per-view="1.15"
          :space-between="16"
          :breakpoints="{
            420: { slidesPerView: 1.35, spaceBetween: 16 },
            520: { slidesPerView: 1.65, spaceBetween: 16 },
            640: { slidesPerView: 2.05, spaceBetween: 18 },
            768: { slidesPerView: 2.35, spaceBetween: 18 },
            960: { slidesPerView: 3.05, spaceBetween: 18 },
            1200: { slidesPerView: 4, spaceBetween: 18 },
          }"
          @swiper="setSwiper"
          @slideChange="updateNavigation"
          @resize="updateNavigation"
        >
          <SwiperSlide v-for="provider in providerCards" :key="provider.name">
            <article class="top-providers__card">
              <div class="top-providers__logo">
                <img :src="asset(provider.logo)" :alt="provider.name" />
              </div>

              <div class="top-providers__games">
                <img
                  v-for="game in provider.games"
                  :key="`${provider.name}-${game}`"
                  :src="asset(`providers/game-${game}.png`)"
                  alt=""
                  aria-hidden="true"
                />
              </div>

              <button class="top-providers__show-all" type="button">Show All</button>
            </article>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </section>
</template>

<style scoped>
.top-providers {
  width: 100%;
}

.top-providers__inner {
  width: min(100%, 1280px);
  margin: 0 auto;
  padding: 12px 16px 46px;
  overflow: hidden;
}

.top-providers__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.top-providers__title {
  margin: 0;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
}

.top-providers__actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.top-providers__arrow,
.top-providers__show-all {
  appearance: none;
  border: 0;
  border-radius: 5px;
  color: #fff;
}

.top-providers__arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 1px solid transparent;
  background:
    var(--gradient-background) padding-box,
    var(--gradient-border) border-box;
  line-height: 0;
  transition: background 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.top-providers__arrow:disabled {
  cursor: default;
  opacity: 0.55;
}

.top-providers__arrow:not(:disabled):hover {
  background:
    var(--gradient-background-hover) padding-box,
    var(--gradient-border-hover) border-box;
  box-shadow: var(--shadow-control-hover);
}

.top-providers__arrow-icon {
  display: block;
  width: 13px;
  height: 13px;
  border-top: 5px solid currentColor;
  border-right: 5px solid currentColor;
}

.top-providers__arrow-icon--prev {
  transform: translateX(2px) rotate(-135deg);
}

.top-providers__arrow-icon--next {
  transform: translateX(-2px) rotate(45deg);
}

.top-providers__slider {
  overflow: visible;
}

.top-providers__slider-frame {
  --app-swiper-fade-offset: 16px;
}

.top-providers__slider :deep(.swiper-slide) {
  height: auto;
}

.top-providers__card {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 360px;
  padding: 26px 14px 18px;
  border: 1px solid rgb(255 255 255 / 0.28);
  border-radius: 5px;
  background: #0d0732;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.top-providers__card:hover {
  transform: translateY(-4px);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.22);
  border-color: rgba(255, 255, 255, 0.45);
}

.top-providers__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  margin-bottom: 20px;
}

.top-providers__logo img {
  width: min(74%, 170px);
  max-height: 42px;
  object-fit: contain;
}

.top-providers__games {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 9px;
  margin-bottom: 22px;
}

.top-providers__games img {
  display: block;
  width: 100%;
  aspect-ratio: 1;
  border: 1px solid rgb(255 211 52 / 0.85);
  border-radius: 5px;
  object-fit: cover;
  background: #081333;
}

.top-providers__show-all {
  width: 100%;
  min-height: 54px;
  margin-top: auto;
  padding: 0 18px;
  background: var(--color-accent);
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
  font-size: 1.05rem;
  font-weight: 500;
  line-height: 1;
  text-transform: uppercase;
}

.top-providers__show-all:hover {
  background-color: var(--color-accent-hover);
  box-shadow: var(--shadow-accent-hover);
}

@media (max-width: 520px) {
  .top-providers__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .top-providers__actions {
    width: 100%;
    justify-content: flex-end;
  }

  .top-providers__card {
    min-height: 380px;
  }
}

@media (min-width: 768px) {
  .top-providers__inner {
    padding: 22px 48px 64px;
  }

  .top-providers__header {
    margin-bottom: 28px;
  }

  .top-providers__title {
    font-size: 1.5rem;
  }

  .top-providers__arrow {
    width: 44px;
    height: 44px;
  }

  .top-providers__slider-frame {
    --app-swiper-fade-offset: 48px;
  }
}
</style>
